import { useState, useCallback, useEffect } from 'react';
import { useWebSocket } from '../hooks/useWebSocket';

const SESSION_ICON = { Race:'🏁', Qualifying:'⏱', Practice:'🔧', Sprint:'⚡', 'Sprint Qualifying':'⚡' };

function rcClass(msg) {
  const m = (msg.message || '').toUpperCase();
  if (m.includes('VIRTUAL SAFETY CAR')) return 'vsc';
  if (m.includes('SAFETY CAR'))         return 'sc';
  if (m.includes('RED FLAG') || m.includes('CHEQUERED')) return 'flag';
  if (m.includes('PENALTY') || m.includes('DRIVE THROUGH')) return 'penalty';
  return '';
}

function WeatherBar({ w }) {
  if (!w) return null;
  return (
    <div className="f1-weather">
      {[
        { val: w.airTemp?.toFixed(0)+'°',   lbl:'Air' },
        { val: w.trackTemp?.toFixed(0)+'°', lbl:'Track' },
        { val: w.humidity?.toFixed(0)+'%',  lbl:'Humidity' },
        { val: w.windSpeed?.toFixed(1),      lbl:'Wind m/s' },
        { val: w.rainfall > 0 ? '🌧️' : '☀️', lbl: w.rainfall > 0 ? 'Rain' : 'Dry' },
      ].map(({val,lbl}) => (
        <div key={lbl} className="f1-weather-item">
          <span className="f1-weather-val">{val||'—'}</span>
          <span className="f1-weather-lbl">{lbl}</span>
        </div>
      ))}
    </div>
  );
}

export default function F1Live({ onBack }) {
  const [snap, setSnap]         = useState(null);
  const [sessions, setSessions] = useState([]);
  const [view, setView]         = useState('live');
  const [loading, setLoading]   = useState(true);
  const [lastUpdate, setLastUpdate] = useState(null);

  const onMsg = useCallback((msg) => {
    if (msg.type === 'F1_SNAPSHOT') {
      setSnap(msg.snapshot);
      if (msg.snapshot?.session || msg.snapshot?.sessionList?.length > 0) setLoading(false);
      setLastUpdate(new Date());
    }
    if (msg.type === 'F1_SESSIONS') setSessions(msg.sessions);
  }, []);

  const { send } = useWebSocket(onMsg);
  useEffect(() => {
    send('F1_GET_SESSIONS');
    send('F1_GET_SNAPSHOT');
    const retry  = setTimeout(() => { send('F1_GET_SESSIONS'); send('F1_GET_SNAPSHOT'); }, 6000);
    const giveup = setTimeout(() => setLoading(false), 15000);
    return () => { clearTimeout(retry); clearTimeout(giveup); };
  }, [send]);

  const session   = snap?.session;
  const lb        = snap?.leaderboard || [];
  const weather   = snap?.weather;
  const rcMsgs    = snap?.rcMessages || [];
  const now       = Date.now();
  const dur       = { Race:2*3600*1000, Qualifying:75*60*1000, Practice:90*60*1000, Sprint:60*60*1000 };
  const sStart    = session ? new Date(session.dateStart).getTime() : 0;
  const sEnd      = session ? (session.dateEnd ? new Date(session.dateEnd).getTime() : sStart+(dur[session.sessionType]||90*60*1000)) : 0;
  const isLive    = session && now >= sStart && now <= sEnd+30*60*1000;
  const isUpcoming= session && now < sStart;

  // ── Calendar browse ───────────────────────────────────────────────────────
  if (view === 'sessions') {
    const meetingMap = {};
    sessions.forEach(s => {
      const key = s.meetingKey || s.meetingName;
      if (!meetingMap[key]) meetingMap[key] = [];
      meetingMap[key].push(s);
    });
    const sortedMeetings = Object.entries(meetingMap)
      .map(([key, sess]) => ({
        name: sess[0].meetingName, country: sess[0].countryName,
        sessions: [...sess].sort((a,b) => new Date(a.dateStart)-new Date(b.dateStart)),
        firstStart: Math.min(...sess.map(s => new Date(s.dateStart).getTime())),
        lastEnd:    Math.max(...sess.map(s => {
          const st = new Date(s.dateStart).getTime();
          return s.dateEnd ? new Date(s.dateEnd).getTime() : st+(dur[s.sessionType]||90*60*1000);
        })),
      }))
      .sort((a,b) => a.firstStart - b.firstStart);

    return (
      <div className="layer" style={{display:'flex',flexDirection:'column',minHeight:'100vh'}}>
        <div className="g-hdr f1-hdr">
          <button className="g-back" onClick={onBack}>← Back</button>
          <div className="g-title" style={{color:'#e8002d'}}>2026 CALENDAR</div>
          <button onClick={()=>setView('live')} style={{padding:'3px 10px',borderRadius:6,border:'1px solid #e8002d',fontSize:'0.65rem',cursor:'pointer',background:'transparent',color:'#e8002d'}}>← Live</button>
        </div>
        <div className="f1-live-wrap">
          {sortedMeetings.length===0&&<p style={{color:'#4a6a9e',textAlign:'center',fontSize:'0.8rem'}}>Loading calendar...</p>}
          {sortedMeetings.map(meeting => {
            const isLiveWk  = now>=meeting.firstStart && now<=meeting.lastEnd;
            const isUpcomWk = now<meeting.firstStart;
            const isPastWk  = now>meeting.lastEnd;
            const raceS     = meeting.sessions.find(s=>s.sessionType==='Race')||meeting.sessions[meeting.sessions.length-1];
            const bCol      = isLiveWk?'#e8002d':isUpcomWk?'#0369a1':'#1e3a6e';
            return (
              <div key={meeting.name} style={{borderRadius:12,overflow:'hidden',border:`1px solid ${bCol}`,opacity:isPastWk?0.6:1,background:'var(--surface,#0a1628)'}}>
                <div style={{padding:'11px 14px',background:isLiveWk?'rgba(232,0,45,0.08)':isUpcomWk?'rgba(3,105,161,0.05)':'var(--surface,#0f1e38)',borderBottom:'1px solid var(--border,#1e3a6e)',display:'flex',alignItems:'flex-start',justifyContent:'space-between',gap:8}}>
                  <div>
                    <div style={{fontSize:'0.98rem',fontWeight:700,color:isLiveWk?'#e8002d':isUpcomWk?'var(--text,#e8edf8)':'var(--sub,#8ba3cc)'}}>{meeting.country}</div>
                    <div style={{fontSize:'0.74rem',color:'var(--sub,#8ba3cc)',marginTop:1}}>{meeting.name}</div>
                    <div style={{fontSize:'0.6rem',color:'var(--sub,#4a6a9e)',marginTop:3}}>{new Date(raceS.dateStart).toLocaleDateString([],{weekday:'short',day:'numeric',month:'long',year:'numeric'})}</div>
                  </div>
                  <span style={{fontSize:'0.5rem',fontWeight:700,letterSpacing:'0.12em',padding:'2px 7px',borderRadius:4,border:`1px solid ${bCol}`,color:bCol,background:`${bCol}18`,whiteSpace:'nowrap',marginTop:2,flexShrink:0}}>
                    {isLiveWk?'● LIVE':isUpcomWk?'UPCOMING':'COMPLETED'}
                  </span>
                </div>
                {meeting.sessions.map((s,si)=>{
                  const sS=new Date(s.dateStart).getTime();
                  const sE=s.dateEnd?new Date(s.dateEnd).getTime():sS+(dur[s.sessionType]||90*60*1000);
                  const sLive=now>=sS&&now<=sE, sPast=now>sE;
                  return(
                    <div key={s.sessionKey} onClick={()=>{send('F1_LOAD_SESSION',{sessionKey:s.sessionKey});setView('live');setLoading(true);}}
                      style={{display:'flex',alignItems:'center',gap:10,padding:'9px 14px',cursor:'pointer',borderBottom:si<meeting.sessions.length-1?'1px solid var(--border,#0f1e38)':'none',background:sLive?'rgba(232,0,45,0.06)':'transparent',transition:'background 0.15s'}}
                      onMouseEnter={e=>e.currentTarget.style.background='rgba(232,0,45,0.1)'}
                      onMouseLeave={e=>e.currentTarget.style.background=sLive?'rgba(232,0,45,0.06)':'transparent'}>
                      <span style={{fontSize:'1rem',width:22,textAlign:'center',flexShrink:0}}>{SESSION_ICON[s.sessionType]||'🏎️'}</span>
                      <div style={{flex:1}}>
                        <div style={{fontSize:'0.8rem',fontWeight:600,color:sLive?'#e8002d':sPast?'var(--sub,#4a6a9e)':'var(--text,#e8edf8)'}}>{s.sessionName}</div>
                        <div style={{fontSize:'0.6rem',color:'var(--sub,#4a6a9e)'}}>{new Date(s.dateStart).toLocaleString([],{weekday:'short',day:'numeric',month:'short',hour:'2-digit',minute:'2-digit'})}</div>
                      </div>
                      {sLive&&<span style={{fontSize:'0.52rem',color:'#e8002d',fontWeight:700,animation:'pulse 1.5s ease infinite'}}>● LIVE</span>}
                      {!sLive&&!sPast&&<span style={{fontSize:'0.52rem',color:'#38bdf8'}}>UPCOMING</span>}
                      {sPast&&!sLive&&<span style={{fontSize:'0.52rem',color:'var(--sub,#3a5a8e)'}}>FIN</span>}
                      <span style={{color:'var(--sub,#2a3a5e)',fontSize:'0.65rem'}}>›</span>
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // ── Live session view ──────────────────────────────────────────────────────
  return (
    <div className="layer" style={{display:'flex',flexDirection:'column',minHeight:'100vh'}}>
      <div className="g-hdr f1-hdr">
        <button className="g-back" onClick={onBack}>← Back</button>
        <div className="g-title" style={{color:'#e8002d'}}>F1 {isLive?'● LIVE':isUpcoming?'UPCOMING':'TIMING'}</div>
        <div style={{display:'flex',gap:6,alignItems:'center'}}>
          {lastUpdate&&<span style={{fontSize:'0.52rem',color:'var(--sub,#3a5a8e)'}}>{lastUpdate.toLocaleTimeString()}</span>}
          <button onClick={()=>{setView('sessions');send('F1_GET_SESSIONS');}}
            style={{padding:'3px 10px',borderRadius:6,border:'1px solid var(--border,#1e3a6e)',fontSize:'0.65rem',cursor:'pointer',background:'transparent',color:'var(--sub,#4a6a9e)'}}>
            Calendar
          </button>
        </div>
      </div>

      <div className="f1-live-wrap">
        {loading&&<p style={{color:'var(--sub,#8ba3cc)',textAlign:'center',fontSize:'0.8rem',padding:24}}>Loading F1 data...</p>}

        {!loading&&!session&&(
          <div style={{textAlign:'center',padding:32,display:'flex',flexDirection:'column',alignItems:'center',gap:10}}>
            <div style={{fontSize:'3rem'}}>🏎️</div>
            <p style={{color:'var(--sub,#8ba3cc)',fontSize:'0.84rem',fontWeight:600}}>No session data available</p>
            <p style={{color:'var(--sub,#4a6a9e)',fontSize:'0.72rem',lineHeight:1.6,maxWidth:300}}>
              Live position data requires no auth and works for all sessions.<br/>
              If OpenF1 is returning no data, try selecting a completed race from the calendar.
            </p>
            <div style={{display:'flex',gap:8,flexWrap:'wrap',justifyContent:'center'}}>
              <button className="f1-standings-tab active" onClick={()=>{setView('sessions');send('F1_GET_SESSIONS');}}>Browse Calendar →</button>
              <button className="f1-standings-tab" onClick={()=>{setLoading(true);send('F1_GET_SESSIONS');send('F1_GET_SNAPSHOT');}}>↻ Retry</button>
            </div>
          </div>
        )}

        {!loading&&session&&<>
          {/* Session header */}
          <div style={{background:'var(--surface,#0f1e38)',border:`1px solid ${isLive?'#e8002d':isUpcoming?'#0369a1':'var(--border,#1e3a6e)'}`,borderRadius:12,padding:'12px 14px'}}>
            <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:4}}>
              <span style={{fontSize:'0.58rem',letterSpacing:'0.15em',color:'var(--sub,#4a6a9e)',textTransform:'uppercase'}}>{session.meetingName} · {session.countryName}</span>
              <span style={{fontSize:'0.55rem',fontWeight:700,padding:'2px 8px',borderRadius:4,
                border:`1px solid ${isLive?'#e8002d':isUpcoming?'#0369a1':'var(--border,#3a5a8e)'}`,
                color:isLive?'#e8002d':isUpcoming?'#38bdf8':'var(--sub,#3a5a8e)',
                animation:isLive?'pulse 1.5s ease infinite':undefined}}>
                {isLive?'● LIVE':isUpcoming?'⏰ UPCOMING':'ENDED'}
              </span>
            </div>
            <div style={{display:'flex',alignItems:'center',gap:10}}>
              <span style={{fontSize:'1.4rem'}}>{SESSION_ICON[session.sessionType]||'🏎️'}</span>
              <span style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:'1.5rem',color:'#e8002d',letterSpacing:'0.06em'}}>{session.sessionName}</span>
            </div>
            {isUpcoming&&<div style={{fontSize:'0.64rem',color:'#38bdf8',marginTop:4}}>
              {new Date(session.dateStart).toLocaleString([],{weekday:'long',day:'numeric',month:'long',hour:'2-digit',minute:'2-digit'})}
            </div>}
          </div>

          {/* Weather */}
          <WeatherBar w={weather}/>

          {/* Race control messages */}
          {rcMsgs.length > 0 && (
            <div>
              <div style={{fontSize:'0.55rem',letterSpacing:'0.18em',color:'var(--sub,#4a6a9e)',textTransform:'uppercase',marginBottom:5}}>Race Control</div>
              <div className="f1-rc">
                {rcMsgs.slice(0,6).map((m,i) => (
                  <div key={i} className={'f1-rc-msg '+rcClass(m)}>
                    <span className="f1-rc-time">L{m.lap||'—'}</span>
                    <span style={{fontSize:'0.72rem'}}>{m.message}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Leaderboard */}
          {lb.length > 0 ? (
            <div className="f1-leaderboard">
              <div className="f1-lb-hdr">
                <span style={{width:26}}>P</span>
                <span style={{width:4}}/>
                <span style={{flex:1}}>Driver</span>
                <span style={{minWidth:80}}>Team</span>
              </div>
              {lb.map((d,i) => (
                <div key={d.driverNumber||i} className="f1-lb-row">
                  <span className={'f1-pos '+(i===0?'p1':i===1?'p2':i===2?'p3':'other')}>{d.position}</span>
                  <div className="f1-team-bar" style={{background:d.teamColour||'#3a5a8e'}}/>
                  <div className="f1-driver" style={{flex:1}}>
                    <span className="f1-driver-name">{d.acronym}</span>
                    <span className="f1-driver-team">{d.fullName}</span>
                  </div>
                  <span style={{fontSize:'0.65rem',color:'var(--sub,#4a6a9e)',minWidth:80}}>{d.teamName?.split(' ').slice(-1)[0]}</span>
                </div>
              ))}
              <div style={{padding:'6px 10px',background:'rgba(0,0,0,0.2)',fontSize:'0.52rem',color:'var(--sub,#3a5a8e)'}}>
                Position data via OpenF1 · updates every 30s · {lastUpdate?.toLocaleTimeString()}
              </div>
            </div>
          ) : (
            <div style={{textAlign:'center',padding:20}}>
              <p style={{color:'var(--sub,#8ba3cc)',fontSize:'0.8rem'}}>No position data yet for this session</p>
              <p style={{color:'var(--sub,#4a6a9e)',fontSize:'0.7rem',marginTop:4}}>Position data is available during and after sessions</p>
            </div>
          )}
        </>}
      </div>
    </div>
  );
}
